import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { GraduationCap, Users, Star, MapPin, Award, Shield, PenTool as Tool, Zap, Calendar, FileCheck, ArrowRight, Book, CreditCard, Building2, Clock, FileText, AlignCenterVertical as Certificate, Target, ChevronRight, MessageSquare } from 'lucide-react';

const Training: React.FC = () => {
  useEffect(() => {
    document.title = 'Formation | Proxy Emobility';
  }, []);

  const [selectedCategory, setSelectedCategory] = useState<'all' | 'individual' | 'professional'>('all');

  const stats = [
    { value: '1000+', label: 'Personnes Formées', icon: <Users size={20} /> },
    { value: '98%', label: 'Taux de Satisfaction', icon: <Star size={20} /> },
    { value: '4', label: 'Centres de Formation', icon: <MapPin size={20} /> },
    { value: '12', label: 'Formateurs Experts', icon: <Award size={20} /> }
  ];

  const courses = [
    {
      id: 'technical',
      category: 'professional',
      title: 'Formation Technique des GIE',
      duration: '5 jours',
      price: '150,000 FCFA',
      description: 'Formation approfondie pour les groupements d\'intérêt économique sur la maintenance et l\'exploitation des motos électriques.',
      level: 'Professionnel',
      rating: 4.9,
      nextSession: '1 Juin 2024',
      location: 'Centre Technique Dakar',
      topics: [
        'Diagnostic et maintenance préventive',
        'Réparation des systèmes électriques',
        'Gestion de la batterie et recharge',
        'Sécurité et normes électriques',
        'Outils et équipements spécialisés'
      ],
      benefits: [
        'Certification technique',
        'Kit d\'outils professionnel',
        'Support technique 6 mois',
        'Documentation complète'
      ],
      icon: <Tool size={24} className="text-primary-600" />,
      image: 'https://images.pexels.com/photos/8993561/pexels-photo-8993561.jpeg'
    },
    {
      id: 'management',
      category: 'professional',
      title: 'Formation en Gestion et Maintenance',
      duration: '4 jours',
      price: '120,000 FCFA',
      description: 'Programme complet sur la gestion efficace d\'une flotte de motos électriques et leur maintenance régulière.',
      level: 'Intermédiaire',
      rating: 4.8,
      nextSession: '15 Juin 2024',
      location: 'Centre de Formation Dakar',
      topics: [
        'Planification de la maintenance',
        'Gestion des stocks de pièces',
        'Suivi des performances',
        'Optimisation des coûts',
        'Reporting et documentation'
      ],
      benefits: [
        'Logiciel de gestion',
        'Templates de documents',
        'Support continu',
        'Réseau professionnel'
      ],
      icon: <Building2 size={24} className="text-primary-600" />,
      image: 'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg'
    },
    {
      id: 'business',
      category: 'professional',
      title: 'Formation en Gestion de Micro-entreprise',
      duration: '3 jours',
      price: '100,000 FCFA',
      description: 'Formation dédiée aux entrepreneurs pour développer et gérer une activité basée sur la mobilité électrique.',
      level: 'Débutant-Intermédiaire',
      rating: 4.9,
      nextSession: '22 Juin 2024',
      location: 'Centre de Formation Dakar',
      topics: [
        'Business planning',
        'Gestion financière',
        'Marketing digital',
        'Service client',
        'Développement commercial'
      ],
      benefits: [
        'Plan d\'affaires type',
        'Outils de gestion',
        'Mentorat 3 mois',
        'Réseau d\'entrepreneurs'
      ],
      icon: <CreditCard size={24} className="text-primary-600" />,
      image: 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg'
    },
    {
      id: 'digital',
      category: 'professional',
      title: 'Formation Plateforme Digitale',
      duration: '2 jours',
      price: '75,000 FCFA',
      description: 'Maîtrisez notre plateforme digitale pour optimiser la gestion de votre activité de mobilité électrique.',
      level: 'Tous niveaux',
      rating: 4.7,
      nextSession: '29 Juin 2024',
      location: 'Centre Digital Dakar',
      topics: [
        'Navigation et interface',
        'Gestion des réservations',
        'Suivi des véhicules',
        'Analyse des données',
        'Reporting automatisé'
      ],
      benefits: [
        'Accès premium 6 mois',
        'Support technique',
        'Mises à jour gratuites',
        'Communauté en ligne'
      ],
      icon: <Zap size={24} className="text-primary-600" />,
      image: 'https://images.pexels.com/photos/3183183/pexels-photo-3183183.jpeg'
    }
  ];

  const filteredCourses = selectedCategory === 'all' 
    ? courses 
    : courses.filter(course => course.category === selectedCategory);

  const features = [
    {
      icon: <Certificate size={24} className="text-primary-600" />,
      title: "Certification Officielle",
      description: "Obtenez une certification reconnue validant vos compétences"
    },
    {
      icon: <Users size={24} className="text-primary-600" />,
      title: "Formateurs Experts",
      description: "Une équipe de professionnels expérimentés"
    },
    {
      icon: <Target size={24} className="text-primary-600" />,
      title: "Formation Pratique",
      description: "80% de pratique pour une maîtrise optimale"
    },
    {
      icon: <Shield size={24} className="text-primary-600" />,
      title: "Suivi Personnalisé",
      description: "Accompagnement individuel tout au long de la formation"
    }
  ];

  return (
    <div className="pt-20">
      <Helmet>
        <title>Formation | Proxy Academy - Développez vos compétences en mobilité électrique</title>
        <meta name="description" content="Développez vos compétences en mobilité électrique avec nos formations certifiantes. Programmes adaptés pour les professionnels, entrepreneurs et techniciens." />
        <meta name="keywords" content="formation mobilité électrique, Proxy Academy, certification technique, gestion de flotte, maintenance motos électriques, formation professionnelle, Afrique" />
        <link rel="canonical" href="https://proxyemobility.com/training" />
        
        {/* Schema.org structured data for Course */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "ItemList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "item": {
                    "@type": "Course",
                    "name": "Formation Technique des GIE",
                    "description": "Formation approfondie pour les groupements d'intérêt économique sur la maintenance et l'exploitation des motos électriques.",
                    "provider": {
                      "@type": "Organization",
                      "name": "Proxy Academy",
                      "sameAs": "https://proxyemobility.com/"
                    },
                    "offers": {
                      "@type": "Offer",
                      "price": "150000",
                      "priceCurrency": "XOF"
                    }
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "item": {
                    "@type": "Course",
                    "name": "Formation en Gestion et Maintenance",
                    "description": "Programme complet sur la gestion efficace d'une flotte de motos électriques et leur maintenance régulière.",
                    "provider": {
                      "@type": "Organization",
                      "name": "Proxy Academy",
                      "sameAs": "https://proxyemobility.com/"
                    },
                    "offers": {
                      "@type": "Offer",
                      "price": "120000",
                      "priceCurrency": "XOF"
                    }
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "item": {
                    "@type": "Course",
                    "name": "Formation en Gestion de Micro-entreprise",
                    "description": "Formation dédiée aux entrepreneurs pour développer et gérer une activité basée sur la mobilité électrique.",
                    "provider": {
                      "@type": "Organization",
                      "name": "Proxy Academy",
                      "sameAs": "https://proxyemobility.com/"
                    },
                    "offers": {
                      "@type": "Offer",
                      "price": "100000",
                      "priceCurrency": "XOF"
                    }
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 4,
                  "item": {
                    "@type": "Course",
                    "name": "Formation Plateforme Digitale",
                    "description": "Maîtrisez notre plateforme digitale pour optimiser la gestion de votre activité de mobilité électrique.",
                    "provider": {
                      "@type": "Organization",
                      "name": "Proxy Academy",
                      "sameAs": "https://proxyemobility.com/"
                    },
                    "offers": {
                      "@type": "Offer",
                      "price": "75000",
                      "priceCurrency": "XOF"
                    }
                  }
                }
              ]
            }
          `}
        </script>
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-primary-900 text-white py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,_rgba(255,255,255,0.1)_0%,_transparent_70%)]"></div>
        <div className="container mx-auto px-4 md:px-6 relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Centre de Formation Proxy Academy
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Développez vos compétences en mobilité électrique avec nos formations certifiantes. 
              Des programmes adaptés pour les professionnels et les entrepreneurs.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 rounded-full mb-4">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Pourquoi Choisir Proxy Academy ?
            </h2>
            <p className="text-lg text-gray-600">
              Une formation d'excellence pour maîtriser la mobilité électrique
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg border border-primary-100 hover:border-primary-200 transition-all hover:shadow-xl group">
                <div className="mb-4 transform group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section id="courses" className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Nos Formations
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Des programmes adaptés aux besoins des professionnels de la mobilité électrique
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {filteredCourses.map((course) => (
              <div 
                key={course.id}
                id={course.id}
                className="bg-white rounded-xl shadow-lg border border-primary-100 hover:border-primary-200 transition-all hover:shadow-xl overflow-hidden"
              >
                <div className="aspect-w-16 aspect-h-9">
                  <img
                    src={course.image}
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary-50 rounded-lg">
                        {course.icon}
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-gray-900">{course.title}</h4>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <span className="flex items-center">
                            <Star size={16} className="text-yellow-400 fill-current mr-1" />
                            {course.rating}
                          </span>
                          <span>•</span>
                          <span>{course.level}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-primary-600 font-bold">{course.price}</div>
                      <div className="text-sm text-gray-500">{course.duration}</div>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-4">{course.description}</p>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="flex items-center text-gray-600">
                      <Calendar size={18} className="mr-2 text-primary-500" />
                      <span className="text-sm">Prochaine session:<br/>{course.nextSession}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <MapPin size={18} className="mr-2 text-primary-500" />
                      <span className="text-sm">{course.location}</span>
                    </div>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div>
                      <h5 className="font-medium text-gray-900 mb-2 flex items-center">
                        <Book size={16} className="mr-2" />
                        Programme
                      </h5>
                      <ul className="space-y-2">
                        {course.topics.slice(0, 3).map((topic, index) => (
                          <li key={index} className="flex items-start">
                            <FileCheck size={16} className="text-primary-500 mr-2 mt-1 flex-shrink-0" />
                            <span className="text-gray-600 text-sm">{topic}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h5 className="font-medium text-gray-900 mb-2 flex items-center">
                        <Award size={16} className="mr-2" />
                        Avantages
                      </h5>
                      <div className="grid grid-cols-2 gap-2">
                        {course.benefits.map((benefit, index) => (
                          <div 
                            key={index}
                            className="flex items-center p-2 bg-primary-50 rounded-lg text-sm text-primary-700"
                          >
                            <Shield size={14} className="mr-2" />
                            {benefit}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <Link
                      to={`/contact?course=${course.id}`}
                      className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      S'inscrire
                      <ArrowRight size={20} className="ml-2" />
                    </Link>
                    <Link
                      to={`/contact?course=${course.id}&type=info`}
                      className="inline-flex items-center px-6 py-3 border border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                    >
                      Plus d'infos
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-900 text-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">
              Prêt à Développer vos Compétences ?
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Inscrivez-vous à l'une de nos formations et rejoignez la révolution de la mobilité électrique.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact"
                className="px-8 py-4 bg-accent-300 hover:bg-accent-400 text-primary-900 font-medium rounded-xl transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                S'inscrire à une formation
                <ArrowRight size={20} className="ml-2 inline-block" />
              </Link>
              <Link 
                to="/contact?type=info"
                className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all border border-white/30"
              >
                Demander plus d'informations
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Training;